import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { MotionConfig } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import ServicesPage from './pages/Services';
import Jobs from './pages/Jobs';
import Values from './pages/Values';
import TechnologiesPage from './pages/Technologies';
import Contact from './pages/Contact';
import BookCall from './pages/BookCall';
import Purchase from './pages/Purchase';
import Payment from './pages/Payment';
import Activities from './pages/Activities';
import ActivityDetail from './pages/ActivityDetail';
import LegalNotice from './pages/LegalNotice';
import Terms from './pages/Terms';
import FAQ from './pages/FAQ';
import Thanks from './pages/Thanks';
import Waiting from './pages/Waiting';
import StatusHandler from './pages/StatusHandler';
import PaymentMethods from './pages/PaymentMethods';
import KlarnaInfo from './pages/KlarnaInfo';
import QuoteCheckout from './pages/QuoteCheckout';
import OrderSummary from './pages/OrderSummary';
import InvoiceDocument from './pages/InvoiceDocument';
import QuoteDocument from './pages/QuoteDocument';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import ScrollToTop from './components/ScrollToTop';
import { absoluteUrl, SITE_URL } from './lib/seo';
import { trackPageView } from './lib/analytics';
import { trackNaturaVisitor } from './lib/naturaVisitorTracking';
import PrivacyConsentBanner from './components/PrivacyConsentBanner';

function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute) {
      return;
    }

    trackPageView(`${location.pathname}${location.search}`);
    trackNaturaVisitor(`${location.pathname}${location.search}`);
  }, [isAdminRoute, location.pathname, location.search]);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'Couteau Suisse Digital',
    legalName: 'Tybox Network',
    url: SITE_URL,
    logo: absoluteUrl('/swiss-army-knife.svg'),
    email: 'contact@couteausuissedigital.com',
    telephone: '+4179653800',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Rue du college 4',
      postalCode: '1400',
      addressLocality: 'Yverdon-les-Bains',
      addressCountry: 'CH',
    },
    areaServed: 'CH',
    sameAs: [
      'https://www.facebook.com/couteausuissedigital',
      'https://www.instagram.com/couteausuissedigital_officiel/',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'Couteau Suisse Digital',
    url: SITE_URL,
    inLanguage: 'fr-CH',
    publisher: { '@id': `${SITE_URL}/#organization` },
  };

  return (
    <>
      {!isAdminRoute && <Header />}
      {!isAdminRoute && <PrivacyConsentBanner />}
      <Routes>
        <Route path="/" element={
          <>
            <Helmet>
              <title>Couteau Suisse Digital - Votre Partenaire Numérique</title>
              <meta name="description" content="Couteau Suisse Digital est votre partenaire pour la transformation digitale. Experts en développement web, marketing digital et solutions numériques." />
              <meta name="keywords" content="développement web, marketing digital, transformation digitale, solutions numériques, agence digitale" />
              <meta property="og:title" content="Couteau Suisse Digital - Votre Partenaire Numérique" />
              <meta property="og:description" content="Experts en développement web, marketing digital et solutions numériques pour votre transformation digitale." />
              <meta property="og:type" content="website" />
              <meta property="og:url" content={SITE_URL} />
              <meta property="og:image" content={absoluteUrl('/icon-512.png')} />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content="Couteau Suisse Digital - Votre Partenaire Numérique" />
              <meta name="twitter:description" content="Experts en développement web, marketing digital et solutions numériques pour votre transformation digitale." />
              <meta name="twitter:image" content={absoluteUrl('/icon-512.png')} />
              <link rel="canonical" href={SITE_URL} />
              <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
              <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
            </Helmet>

            <main>
              <Hero />
              <Services />
              <CTASection />
            </main>
          </>
        } />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/metiers" element={<Jobs />} />
        <Route path="/valeurs" element={<Values />} />
        <Route path="/technologies" element={<TechnologiesPage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/reserver" element={<BookCall />} />
        <Route path="/acheter/:serviceId" element={<Purchase />} />
        <Route path="/payment/:serviceId" element={<Payment />} />
        <Route path="/devis/:paymentLinkKey" element={<QuoteCheckout />} />
        <Route path="/commande/:orderNumber" element={<OrderSummary />} />
        <Route path="/documents/facture/:invoiceNumber" element={<InvoiceDocument />} />
        <Route path="/documents/devis/:quoteNumber" element={<QuoteDocument />} />
        <Route path="/activites" element={<Activities />} />
        <Route path="/activites/:industryId" element={<ActivityDetail />} />
        <Route path="/mentions-legales" element={<LegalNotice />} />
        <Route path="/conditions-generales" element={<Terms />} />
        <Route path="/moyens-de-paiement" element={<PaymentMethods />} />
        <Route path="/klarna-info" element={<KlarnaInfo />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/status-handler" element={<StatusHandler />} />
        <Route path="/merci" element={<Thanks />} />
        <Route path="/attente" element={<Waiting />} />
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin/dashboard" element={<Dashboard />} />
        <Route path="*" element={
          <main className="min-h-screen bg-gray-50 px-4 py-24 text-center">
            <Helmet>
              <title>Page introuvable | Couteau Suisse Digital</title>
              <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <h1 className="text-4xl font-bold text-gray-900">Page introuvable</h1>
            <p className="mt-4 text-gray-600">Cette page n'existe pas ou n'est plus disponible.</p>
            <a className="mt-8 inline-block text-neon-orange underline" href="/">Retour à l'accueil</a>
          </main>
        } />
      </Routes>
      {!isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <MotionConfig reducedMotion="user">
      <Router>
        <ScrollToTop />
        <AppShell />
      </Router>
    </MotionConfig>
  );
}

export default App;
